import type { IncomingMessage } from 'http';
import type { ParsedUrlQuery } from 'querystring';
import Url from 'url';
import { toCloudinarySrc } from '@peloton/images';
import { isDefined } from '@peloton/types/isDefined';
import {
  toLocalizedDayNameLongMonthSlashDay,
  toLocalizedTime,
} from '@engage/internationalize/dateTime';
import {
  toScheduleEventDetailsCopyForLanguage,
  toGenericWorkoutDetailsCopyForLanguage,
  toTagDetailsCopyForLanguage,
  toInviteDetailsCopyForLanguage,
} from './copy';
import type {
  DetailedWorkoutResponse,
  RideDetailsResponse,
  InviteDetailsResponse,
} from './data';
import { setApiEnvQueryParameter, toApiBaseUrl } from './data';
import { meta } from './html';
import { toTime } from './toTime';
import { toUrlFromHostHeader, toLanguageFromHostname } from './url';

const CLOUDINARY_ACCOUNT = 'peloton-cycle';
const APP_ID = '492491817438833';
const DEFAULT_RIDE_IMAGE =
  'https://strava-default-images.s3-us-west-2.amazonaws.com/workout_share_image/default.png';

const FALLBACK_URL = new URL('https://members.onepeloton.com/index.html');

const toUrl = (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  asPath: string | undefined,
): URL => {
  const result = Url.resolve(
    toUrlFromHostHeader(FALLBACK_URL, request).href,
    asPath?.split('?')[0] ?? '',
  );
  const resultURL = new URL(result);
  setApiEnvQueryParameter(request, query, resultURL);
  return resultURL;
};

// replace 'undefined' if process.env is not defined
const toCfFunctionCloudinarySrc = (width: number, src: string | null) => {
  if (!src) {
    return DEFAULT_RIDE_IMAGE;
  }

  return toCloudinarySrc({ src, width }).replace('undefined', CLOUDINARY_ACCOUNT);
};

export const toClassTags = (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  asPath: string | undefined,
  classId: string,
  { ride: { image_url, title, description } }: RideDetailsResponse,
) => {
  const cloudinaryImageUrl = toCfFunctionCloudinarySrc(1200, image_url);

  const classesUrl = toUrl(request, query, asPath);
  classesUrl.searchParams.set('modal', 'classDetailsModal');
  classesUrl.searchParams.set('classId', classId);

  return [
    meta('fb:app_id', APP_ID),
    meta('og:url', classesUrl.href),
    meta('og:title', title),
    meta('og:type', 'fitness.course'),
    meta('og:description', description),
    meta('og:image', cloudinaryImageUrl),
    meta('og:image:height', '630'),
    meta('og:image:width', '1200'),
  ];
};

export const toTagTags = (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  asPath: string | undefined,
  tag: string,
) => {
  const language = toLanguageFromHostname(request);
  const tagName = decodeURI(tag);
  const imageUrl = Url.resolve(
    toApiBaseUrl(request, query),
    `/api/tags/share_tag_image?tag_name=${tag}`,
  ); // Api url does not support unicode
  const shareUrl = toUrl(request, query, asPath);
  const { description, title } = toTagDetailsCopyForLanguage(language, tagName);

  return [
    meta('fb:app_id', APP_ID),
    meta('og:url', shareUrl.href),
    meta('og:title', title),
    meta('og:type', 'website'),
    meta('og:description', description),
    meta('og:image', imageUrl),
    meta('og:image:height', '630'),
    meta('og:image:width', '1200'),
  ];
};

export const toScheduleEventDetailsTags = (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  asPath: string | undefined,
  startTime: string,
  rideResponse: RideDetailsResponse,
) => {
  const language = toLanguageFromHostname(request);
  const { joinScheduledClass } = toScheduleEventDetailsCopyForLanguage(
    language,
    rideResponse.ride.title,
    startTime,
  );
  const classImage = rideResponse.ride.image_url ?? DEFAULT_RIDE_IMAGE;
  const cloudinaryImageUrl = toCfFunctionCloudinarySrc(1200, classImage);
  const url = toUrl(request, query, asPath);

  return [
    meta('fb:app_id', APP_ID),
    meta('og:url', url.href),
    meta('og:title', joinScheduledClass),
    meta('og:type', 'fitness.course'),
    meta('og:description', rideResponse.ride.description),
    meta('og:image', cloudinaryImageUrl),
    meta('og:image:height', '630'),
    meta('og:image:width', '1200'),
  ];
};

export const toDetailedWorkoutTags = (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  asPath: string | undefined,
  shareWorkoutId: string,
  { workout: { share_title, share_description } }: DetailedWorkoutResponse,
) => {
  const workoutUrl = toUrl(request, query, asPath);
  const workoutImageUrl = Url.resolve(
    toApiBaseUrl(request, query),
    `/api/share/workout/metric_graphic/${shareWorkoutId}.png`,
  );

  return [
    meta('fb:app_id', APP_ID),
    meta('og:url', workoutUrl.href),
    meta('og:title', share_title),
    meta('og:type', 'fitness.course'),
    meta('og:description', share_description),
    meta('og:image', workoutImageUrl),
    meta('og:image:height', '630'),
    meta('og:image:width', '1200'),
  ];
};

export const toInviteDetailsTags = (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  asPath: string | undefined,
  inviteDetails: InviteDetailsResponse,
) => {
  const { rideDetails, deserializedScheduleInfo, inviteShareRes } = inviteDetails;
  const language = toLanguageFromHostname(request);

  const time = isDefined(inviteDetails.inviteShareRes.createdTimezone)
    ? toTime(deserializedScheduleInfo.scheduledStartTime).tz(
        inviteDetails.inviteShareRes.createdTimezone,
      )
    : toTime(deserializedScheduleInfo.scheduledStartTime);

  const formattedTimezone = isDefined(inviteDetails.inviteShareRes.createdTimezone)
    ? time.format('z') + ' '
    : '';

  const formattedStart = `${toLocalizedTime(
    time,
  )} ${formattedTimezone}${toLocalizedDayNameLongMonthSlashDay(time)}`;

  let occasionType = 'other';
  if (
    inviteShareRes.translatedOccasionName &&
    inviteShareRes.translatedOccasionName.length
  ) {
    occasionType = 'OCCASION';
  }

  let metaTitle: string;

  if (inviteShareRes.hostUsername) {
    metaTitle = toInviteDetailsCopyForLanguage(
      language,
      inviteShareRes?.translatedOccasionName ?? '',
      inviteShareRes.hostUsername,
      formattedStart,
      occasionType,
    ).inviteDetailsTitle;
  } else {
    metaTitle = toScheduleEventDetailsCopyForLanguage(
      language,
      rideDetails.title,
      formattedStart,
    ).joinScheduledClass;
  }

  const classImage = rideDetails.image_url ?? DEFAULT_RIDE_IMAGE;
  const cloudinaryImageUrl = toCfFunctionCloudinarySrc(1200, classImage);

  const url = toUrl(request, query, asPath);

  return [
    meta('fb:app_id', APP_ID),
    meta('og:url', url.href),
    meta('og:title', metaTitle),
    meta('og:type', 'website'),
    meta('og:description', rideDetails.description),
    meta('og:image', cloudinaryImageUrl),
    meta('og:image:height', '630'),
    meta('og:image:width', '1200'),
    meta('twitter:card', 'summary_large_image'),
  ];
};

const toImageUrl = ({
  incomingMessage,
  query,
  id,
}: {
  incomingMessage: IncomingMessage;
  query: ParsedUrlQuery;
  id: string;
}) =>
  Url.resolve(
    toApiBaseUrl(incomingMessage, query),
    `/api/workout/metric_graphic/${id}.png`,
  );

const toGenericShareableWorkoutProps = ({
  incomingMessage,
  workoutId,
  query,
  asPath,
}: {
  incomingMessage: IncomingMessage;
  workoutId: string;
  query: ParsedUrlQuery;
  asPath: string | undefined;
}) => {
  const url = toUrl(incomingMessage, query, asPath);

  return {
    url: url.href,
    imageUrl: toImageUrl({ incomingMessage, query, id: workoutId }),
  };
};

export const toGenericWorkoutTags = (
  incomingMessage: IncomingMessage,
  query: ParsedUrlQuery,
  asPath: string | undefined,
  workoutId: string,
) => {
  const language = toLanguageFromHostname(incomingMessage);
  const { url, imageUrl } = toGenericShareableWorkoutProps({
    incomingMessage,
    workoutId,
    query,
    asPath,
  });
  const { description, title } = toGenericWorkoutDetailsCopyForLanguage(language);

  return [
    meta('fb:app_id', APP_ID),
    meta('og:url', url),
    meta('og:title', title),
    meta('og:type', 'fitness.course'),
    meta('og:description', description),
    meta('og:image', imageUrl),
    meta('og:image:height', '630'),
    meta('og:image:width', '1200'),
  ];
};
